import { type ClassValue, clsx } from "clsx";
import { type FormatOptions, format, isValid, parseISO } from "date-fns";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function formatGrade(value: string | number | null | undefined): string {
	const num = typeof value === "number" ? value : Number(value);
	if (!Number.isFinite(num)) return "0";
	const rounded = Math.round(num * 100) / 100;
	return rounded.toLocaleString("en-US", {
		maximumFractionDigits: 2,
		useGrouping: false,
	});
}

/**
 * Wrapper seguro para parseISO que não crasha com valores inválidos.
 * Retorna `Invalid Date` em vez de lançar TypeError/RangeError.
 */
export function safeParseISO(dateString: string | null | undefined): Date {
	if (!dateString) return new Date(Number.NaN);
	try {
		const date = parseISO(dateString);
		return Number.isNaN(date.getTime()) ? new Date(Number.NaN) : date;
	} catch {
		return new Date(Number.NaN);
	}
}

/**
 * Interpreta `YYYY-MM-DD` como data de calendário no fuso local.
 * Evita deslocar um dia ao usar `new Date("YYYY-MM-DD")`, que o JS trata como UTC meia-noite.
 */
export function parseLocalDateOnly(
	dateYmdString: string | null | undefined,
): Date {
	if (!dateYmdString || typeof dateYmdString !== "string") {
		return new Date(Number.NaN);
	}

	const numericSegments = dateYmdString
		.split("-")
		.map((segment) => Number.parseInt(segment, 10));

	const hasExactlyThreeParts = numericSegments.length === 3;
	const hasInvalidNumber = numericSegments.some((value) => Number.isNaN(value));
	if (!hasExactlyThreeParts || hasInvalidNumber) {
		return new Date(Number.NaN);
	}

	const [year, calendarMonthOneBased, dayOfMonth] = numericSegments;
	return new Date(year, calendarMonthOneBased - 1, dayOfMonth);
}

/**
 * Wrapper seguro para format que retorna fallback em vez de lançar RangeError.
 */
export function safeFormat(
	date: Date,
	formatStr: string,
	options?: FormatOptions,
	fallback = "—",
): string {
	try {
		if (!date || Number.isNaN(date.getTime())) return fallback;
		return format(date, formatStr, options);
	} catch {
		return fallback;
	}
}

/** Valores em segundos desde o epoch costumam ter magnitude abaixo disso; acima, tratamos como ms. */
const UNIX_SECONDS_UPPER_BOUND = 10_000_000_000;

/**
 * Interpreta datas vindas da API: ISO 8601, unix em segundos ou em milissegundos, ou null.
 * `new Date(null)` vira epoch (1969/1970) — por isso null e 0 são tratados como inválidos aqui.
 */
export function parseApiDate(value: string | number | null | undefined): Date {
	if (value === null || value === undefined) {
		return new Date(Number.NaN);
	}
	if (typeof value === "number") {
		if (!Number.isFinite(value) || value === 0) {
			return new Date(Number.NaN);
		}
		const ms =
			Math.abs(value) < UNIX_SECONDS_UPPER_BOUND ? value * 1000 : value;
		return new Date(ms);
	}
	const s = String(value).trim();
	if (!s) {
		return new Date(Number.NaN);
	}
	const asNum = Number(s);
	if (Number.isFinite(asNum) && /^-?\d+(\.\d+)?$/.test(s)) {
		if (asNum === 0) {
			return new Date(Number.NaN);
		}
		const ms =
			Math.abs(asNum) < UNIX_SECONDS_UPPER_BOUND ? asNum * 1000 : asNum;
		return new Date(ms);
	}
	return safeParseISO(s);
}

export function formatApiDate(
	value: string | number | null | undefined,
	formatStr: string,
	options?: FormatOptions,
	fallback = "—",
): string {
	const d = parseApiDate(value);
	if (!isValid(d) || d.getTime() === 0) {
		return fallback;
	}
	return safeFormat(d, formatStr, options, fallback);
}
